import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Search, MapPin, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';
import { TableSkeleton } from '@/components/ui/Skeletons';

const emptyForm = {
    name: '',
    description: ''
};

const WarehouseLocations = ({ user }) => {
    const [locations, setLocations] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    // User assignment dialog state
    const [usersDialogOpen, setUsersDialogOpen] = useState(false);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [savingUsers, setSavingUsers] = useState(false);

    useEffect(() => {
        fetchLocations();
        if (user?.role === 'admin') fetchUsers();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const fetchLocations = async () => {
        try {
            const res = await axios.get(`${API}/warehouse-locations`);
            setLocations(res.data);
        } catch (error) {
            toast.error('Failed to fetch warehouse locations');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${API}/users`);
            setAllUsers(res.data);
        } catch (error) {
            console.error('Failed to fetch users', error);
        }
    };

    const openCreateDialog = () => {
        setEditingLocation(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEditDialog = (location) => {
        setEditingLocation(location);
        setForm({
            name: location.name || '',
            description: location.description || ''
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            toast.error('Location name is required');
            return;
        }

        setSaving(true);
        try {
            if (editingLocation) {
                await axios.put(`${API}/warehouse-locations/${editingLocation.id}`, form);
                toast.success('Location updated');
            } else {
                await axios.post(`${API}/warehouse-locations`, form);
                toast.success('Location created');
            }
            setDialogOpen(false);
            fetchLocations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save location');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (location) => {
        if (!window.confirm(`Delete location "${location.name}"?`)) return;
        try {
            await axios.delete(`${API}/warehouse-locations/${location.id}`);
            toast.success('Location deleted');
            fetchLocations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete location');
        }
    };

    // Get users assigned to a specific warehouse
    const getUsersForWarehouse = (warehouseName) => {
        return allUsers.filter(u => (u.assigned_warehouses || []).includes(warehouseName));
    };

    // Open user assignment dialog for a warehouse
    const openUsersDialog = (location) => {
        setSelectedWarehouse(location);
        const assignedUsers = getUsersForWarehouse(location.name);
        setSelectedUserIds(assignedUsers.map(u => u.id));
        setUsersDialogOpen(true);
    };

    // Toggle a user's assignment to the selected warehouse
    const toggleUser = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    // Save user assignments for the selected warehouse
    const handleSaveUsers = async () => {
        if (!selectedWarehouse) return;
        setSavingUsers(true);
        try {
            const warehouseName = selectedWarehouse.name;
            // For each user, update their assigned_warehouses
            const nonAdminUsers = allUsers.filter(u => u.role !== 'admin');
            for (const u of nonAdminUsers) {
                const currentWarehouses = u.assigned_warehouses || [];
                const isSelected = selectedUserIds.includes(u.id);
                const isCurrentlyAssigned = currentWarehouses.includes(warehouseName);

                if (isSelected && !isCurrentlyAssigned) {
                    // Add this warehouse to user
                    await axios.put(`${API}/users/${u.id}/warehouses`, {
                        warehouse_names: [...currentWarehouses, warehouseName]
                    });
                } else if (!isSelected && isCurrentlyAssigned) {
                    // Remove this warehouse from user
                    await axios.put(`${API}/users/${u.id}/warehouses`, {
                        warehouse_names: currentWarehouses.filter(w => w !== warehouseName)
                    });
                }
            }
            toast.success('User assignments updated');
            setUsersDialogOpen(false);
            fetchUsers(); // Refresh user data
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update user assignments');
        } finally {
            setSavingUsers(false);
        }
    };

    const filteredLocations = locations.filter(l => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (
            (l.name || '').toLowerCase().includes(q) ||
            (l.description || '').toLowerCase().includes(q)
        );
    });

    const paginatedLocations = filteredLocations.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    if (loading) {
        return (
            <div className="p-8 max-w-7xl mx-auto space-y-6">
                <div className="flex justify-between mb-8">
                    <div>
                        <div className="h-10 w-64 bg-stone-200/60 rounded animate-pulse mb-2"></div>
                        <div className="h-4 w-48 bg-stone-200/60 rounded animate-pulse"></div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm p-6">
                    <TableSkeleton rows={4} columns={4} />
                </div>
            </div>
        );
    }

    const nonAdminUsers = allUsers.filter(u => u.role !== 'admin');

    return (
        <div className="p-8 max-w-7xl mx-auto" data-testid="locations-page">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-stone-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Warehouse Locations
                    </h1>
                    <p className="text-sm text-stone-500 mt-2 font-medium">
                        Manage your warehouse locations
                    </p>
                </div>
                <Button
                    onClick={openCreateDialog}
                    className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow shadow-indigo-200 transition-all font-medium"
                    data-testid="add-location-btn"
                >
                    <Plus size={16} className="mr-2" /> Add Location
                </Button>
            </div>

            <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-stone-100 bg-stone-50/30">
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <Input
                            placeholder="Search by name or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 max-w-sm"
                            data-testid="search-locations"
                        />
                    </div>
                </div>

                {filteredLocations.length === 0 ? (
                    <div className="text-center py-12 text-[hsl(145,20%,40%)]">
                        <MapPin size={48} className="mx-auto mb-4 opacity-40" />
                        <p>{searchTerm ? 'No locations match your search' : 'No locations yet. Add your first location!'}</p>
                    </div>
                ) : (
                    <>
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-stone-50/80 border-b border-stone-100/80">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Description</th>
                                        {user?.role === 'admin' && (
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Assigned Users</th>
                                        )}
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100">
                                    {paginatedLocations.map((location) => {
                                        const assignedUsers = getUsersForWarehouse(location.name);
                                        return (
                                            <tr key={location.id} className="hover:bg-stone-50/50 transition-colors">
                                                <td className="px-4 py-3 text-sm font-medium text-stone-800">{location.name}</td>
                                                <td className="px-4 py-3 text-sm text-stone-600 max-w-[300px] truncate">{location.description || '-'}</td>
                                                {user?.role === 'admin' && (
                                                    <td className="px-4 py-3 text-sm text-stone-600">
                                                        {assignedUsers.length > 0 ? (
                                                            <div className="flex flex-wrap gap-1">
                                                                {assignedUsers.map(u => (
                                                                    <span key={u.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                                        {u.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-stone-400 text-xs">No users assigned</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1">
                                                        {user?.role === 'admin' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => openUsersDialog(location)}
                                                                className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                                                title="Assign Users"
                                                            >
                                                                <Users size={14} />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openEditDialog(location)}
                                                            className="h-8 w-8 p-0"
                                                            data-testid={`edit-location-${location.id}`}
                                                        >
                                                            <Pencil size={14} />
                                                        </Button>
                                                        {user?.role === 'admin' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDelete(location)}
                                                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                                data-testid={`delete-location-${location.id}`}
                                                            >
                                                                <Trash2 size={14} />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="md:hidden flex flex-col divide-y divide-stone-100">
                            {paginatedLocations.map((location) => {
                                const assignedUsers = getUsersForWarehouse(location.name);
                                return (
                                    <div key={`mobile-${location.id}`} className="p-4 bg-white">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h4 className="font-semibold text-stone-800">{location.name}</h4>
                                                <p className="text-sm text-stone-600 mt-1 line-clamp-2">{location.description || 'No description'}</p>
                                            </div>
                                        </div>

                                        {user?.role === 'admin' && (
                                            <div className="mt-3">
                                                <span className="text-xs text-stone-500 block mb-1">Assigned Users</span>
                                                {assignedUsers.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {assignedUsers.map(u => (
                                                            <span key={u.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                                {u.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-stone-400 text-xs italic">None</span>
                                                )}
                                            </div>
                                        )}

                                        <div className="flex gap-2 justify-end mt-4 pt-3 border-t border-stone-100">
                                            {user?.role === 'admin' && (
                                                <Button variant="outline" size="sm" className="h-8 text-xs text-emerald-600 flex-1" onClick={() => openUsersDialog(location)}>
                                                    <Users size={12} className="mr-1.5" /> Assign Users
                                                </Button>
                                            )}
                                            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => openEditDialog(location)}>
                                                <Pencil size={14} className="text-stone-600" />
                                            </Button>
                                            {user?.role === 'admin' && (
                                                <Button variant="outline" size="sm" className="h-8 w-8 p-0 border-red-200 hover:bg-red-50" onClick={() => handleDelete(location)}>
                                                    <Trash2 size={14} className="text-red-500" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-center p-4 border-t border-stone-100 bg-stone-50/30">
                            <Pagination currentPage={currentPage} totalItems={filteredLocations.length} onPageChange={setCurrentPage} />
                        </div>
                    </>
                )}
            </div>

            {/* Add / Edit Location Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg w-[95vw]">
                    <DialogHeader>
                        <DialogTitle>{editingLocation ? 'Edit Location' : 'Add Location'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div>
                            <Label className="mb-1 block">Location Name *</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="E.g., Finished Goods Warehouse"
                                data-testid="location-name-input"
                            />
                        </div>
                        <div>
                            <Label className="mb-1 block">Description</Label>
                            <Input
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder="E.g., Main facility, aisle 4"
                            />
                        </div>
                        <div className="flex flex-col sm:flex-row justify-end gap-2 mt-6 pt-4 border-t border-stone-100">
                            <Button variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow shadow-indigo-200 transition-all"
                                data-testid="save-location-btn"
                            >
                                {saving ? 'Saving...' : (editingLocation ? 'Update Location' : 'Create Location')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* User Assignment Dialog */}
            <Dialog open={usersDialogOpen} onOpenChange={setUsersDialogOpen}>
                <DialogContent className="max-w-md w-[95vw]">
                    <DialogHeader>
                        <DialogTitle>Assign Users to {selectedWarehouse?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 pt-2 max-h-[60vh] overflow-y-auto">
                        {nonAdminUsers.length === 0 ? (
                            <p className="text-sm text-stone-500 text-center py-4">No non-admin users available</p>
                        ) : (
                            nonAdminUsers.map(u => (
                                <label key={u.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 cursor-pointer transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={selectedUserIds.includes(u.id)}
                                        onChange={() => toggleUser(u.id)}
                                        className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-stone-800">{u.name}</div>
                                        <div className="text-xs text-stone-500">{u.role}</div>
                                    </div>
                                </label>
                            ))
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4 pt-4 border-t border-stone-100">
                        <Button variant="outline" onClick={() => setUsersDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
                        <Button
                            onClick={handleSaveUsers}
                            disabled={savingUsers}
                            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {savingUsers ? 'Saving...' : 'Save Assignments'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WarehouseLocations;

